import type { ClipboardEvent } from 'react'
import { useLocation } from 'react-router-dom'
import {
  BankOutlined,
  MobileOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { Alert, Button, Col, Form, Input, Row } from 'antd'
import type {
  SupplierSmsCodeSendParams,
  SupplierSmsLoginParams,
} from '../../api/CommonApi'
import Style from './login.module.scss'
import {
  isSupplierIdentityValid,
  isSupplierLoginValid,
  normalizeSupplierIdentity,
  normalizeSupplierLogin,
  validateEnterpriseName,
  validateMobile,
  validateSmsCode,
} from './validation'

export type LoginNotice = {
  message: string
  type: 'error' | 'success'
}

interface Props {
  countdown: number
  handleClickLogin: (values: SupplierSmsLoginParams) => Promise<void>
  handleSendCode: (values: SupplierSmsCodeSendParams) => Promise<void>
  loginLoading: boolean
  notice?: LoginNotice
  onNoticeClose: () => void
  sendLoading: boolean
}

const createValidator =
  (validator: (value?: string) => string | undefined) =>
  async (_: unknown, value?: string) => {
    const message = validator(value)
    if (message) {
      throw new Error(message)
    }
  }

const Login = ({
  countdown,
  handleClickLogin,
  handleSendCode,
  loginLoading,
  notice,
  onNoticeClose,
  sendLoading,
}: Props) => {
  const [form] = Form.useForm<SupplierSmsLoginParams>()
  const { search } = useLocation()
  const supplierName = new URLSearchParams(search).get('supplierName') ?? ''
  const enterpriseName = Form.useWatch('enterpriseName', form)
  const mobile = Form.useWatch('mobile', form)
  const code = Form.useWatch('code', form)

  const identityValues = { enterpriseName, mobile }
  const loginValues = { ...identityValues, code }
  const canSend =
    isSupplierIdentityValid(identityValues) && countdown === 0 && !sendLoading
  const canLogin = isSupplierLoginValid(loginValues) && !loginLoading

  const rejectOverlongPaste = (
    field: keyof SupplierSmsLoginParams,
    maxLength: number,
    errorMessage: string,
    event: ClipboardEvent<HTMLInputElement>,
  ) => {
    const input = event.currentTarget
    const selectedLength =
      (input.selectionEnd ?? input.value.length) - (input.selectionStart ?? 0)
    const nextLength =
      input.value.length -
      selectedLength +
      event.clipboardData.getData('text').length
    if (nextLength <= maxLength) {
      return
    }
    event.preventDefault()
    form.setFields([{ name: field, errors: [errorMessage] }])
  }

  const onSendCode = async () => {
    try {
      const values = await form.validateFields(['enterpriseName', 'mobile'])
      await handleSendCode(normalizeSupplierIdentity(values))
    } catch {
      return
    }
  }

  const onSubmitLogin = async (values: SupplierSmsLoginParams) => {
    await handleClickLogin(normalizeSupplierLogin(values))
  }

  return (
    <div className={Style.layer}>
      <div className={Style.container}>
        <div className={Style.loginbody}>
          <div className={Style.loginbox}>
            <div
              className={Style.tip}
              style={{
                display:
                  new Date() <=
                  new Date(new Date().getFullYear(), 2, 19, 23, 59, 59)
                    ? 'block'
                    : 'none',
              }}
            >
              <div className={Style['tip-head']}>
                <WarningOutlined /> 【维护通知】
              </div>
              <div className={Style['tip-content']}>
                本系统将于本周四（[03-19]）19:00-22:00
                进行维护，期间服务暂停。期间请您不要使用系统，以免造成损失。对此带来的不便，敬请谅解。
              </div>
            </div>
            <div className={Style.formcontainer}>
              <div className={Style.logintitle}>咸亨国际SCP协同系统</div>
              {notice && (
                <Alert
                  className={Style.statusAlert}
                  closable
                  message={notice.message}
                  onClose={onNoticeClose}
                  showIcon
                  type={notice.type}
                />
              )}
              <Form
                autoComplete="on"
                className={Style.loginForm}
                form={form}
                initialValues={{
                  enterpriseName:
                    supplierName.length <= 200 ? supplierName : '',
                }}
                onFinish={onSubmitLogin}
                onValuesChange={onNoticeClose}
              >
                <Form.Item
                  name="enterpriseName"
                  rules={[
                    { validator: createValidator(validateEnterpriseName) },
                  ]}
                  className={Style.itembox}
                  validateTrigger={['onBlur', 'onSubmit']}
                >
                  <Input
                    autoComplete="on"
                    className={Style.iteminput}
                    maxLength={200}
                    onPaste={(event) =>
                      rejectOverlongPaste(
                        'enterpriseName',
                        200,
                        '供应商抬头最多输入200个字符',
                        event,
                      )
                    }
                    prefix={<BankOutlined className={Style.inputIcon} />}
                    placeholder="请输入供应商抬头"
                  />
                </Form.Item>
                <Form.Item
                  name="mobile"
                  rules={[{ validator: createValidator(validateMobile) }]}
                  className={Style.itembox}
                  validateTrigger={['onBlur', 'onSubmit']}
                >
                  <Input
                    autoComplete="on"
                    className={Style.iteminput}
                    inputMode="numeric"
                    maxLength={11}
                    onPaste={(event) =>
                      rejectOverlongPaste(
                        'mobile',
                        11,
                        '请输入正确的11位手机号',
                        event,
                      )
                    }
                    prefix={<MobileOutlined className={Style.inputIcon} />}
                    placeholder="请输入手机号"
                  />
                </Form.Item>
                <Form.Item className={Style.itembox}>
                  <Row gutter={8}>
                    <Col span={15}>
                      <Form.Item
                        name="code"
                        noStyle
                        rules={[
                          { validator: createValidator(validateSmsCode) },
                        ]}
                        validateTrigger={['onBlur', 'onSubmit']}
                      >
                        <Input
                          className={Style.codeinput}
                          maxLength={6}
                          onPaste={(event) =>
                            rejectOverlongPaste(
                              'code',
                              6,
                              '请输入6位短信验证码',
                              event,
                            )
                          }
                          prefix={
                            <SafetyCertificateOutlined
                              className={Style.inputIcon}
                            />
                          }
                          placeholder="请输入短信验证码"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={9}>
                      <Button
                        className={Style.sendButton}
                        disabled={!canSend}
                        loading={sendLoading}
                        onClick={onSendCode}
                      >
                        {countdown > 0 ? `${countdown}秒后重发` : '发送验证码'}
                      </Button>
                    </Col>
                  </Row>
                </Form.Item>
                <Form.Item noStyle>
                  <Button
                    className={Style.btnbox}
                    disabled={!canLogin}
                    htmlType="submit"
                    loading={loginLoading}
                  >
                    登录
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
import type { ClipboardEvent } from 'react'
import { useLocation } from 'react-router-dom'
import {
  BankOutlined,
  MobileOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { Alert, Button, Col, Form, Input, Row } from 'antd'
import type {
  SupplierSmsCodeSendParams,
  SupplierSmsLoginParams,
} from '../../api/CommonApi'
import Style from './login.module.scss'
import {
  isSupplierIdentityValid,
  isSupplierLoginValid,
  normalizeSupplierIdentity,
  normalizeSupplierLogin,
  validateEnterpriseName,
  validateMobile,
  validateSmsCode,
} from './validation'

export type LoginNotice = {
  message: string
  type: 'error' | 'success'
}

interface Props {
  countdown: number
  handleClickLogin: (values: SupplierSmsLoginParams) => Promise<void>
  handleSendCode: (values: SupplierSmsCodeSendParams) => Promise<void>
  loginLoading: boolean
  notice?: LoginNotice
  onNoticeClose: () => void
  sendLoading: boolean
}

const createValidator =
  (validator: (value?: string) => string | undefined) =>
  async (_: unknown, value?: string) => {
    const message = validator(value)
    if (message) {
      throw new Error(message)
    }
  }

const Login = ({
  countdown,
  handleClickLogin,
  handleSendCode,
  loginLoading,
  notice,
  onNoticeClose,
  sendLoading,
}: Props) => {
  const [form] = Form.useForm<SupplierSmsLoginParams>()
  const { search } = useLocation()
  const supplierName = new URLSearchParams(search).get('supplierName') ?? ''
  const enterpriseName = Form.useWatch('enterpriseName', form)
  const mobile = Form.useWatch('mobile', form)
  const code = Form.useWatch('code', form)

  const identityValues = { enterpriseName, mobile }
  const loginValues = { ...identityValues, code }
  const canSend =
    isSupplierIdentityValid(identityValues) && countdown === 0 && !sendLoading
  const canLogin = isSupplierLoginValid(loginValues) && !loginLoading

  const rejectOverlongPaste = (
    field: keyof SupplierSmsLoginParams,
    maxLength: number,
    errorMessage: string,
    event: ClipboardEvent<HTMLInputElement>,
  ) => {
    const input = event.currentTarget
    const selectedLength =
      (input.selectionEnd ?? input.value.length) - (input.selectionStart ?? 0)
    const nextLength =
      input.value.length -
      selectedLength +
      event.clipboardData.getData('text').length
    if (nextLength <= maxLength) {
      return
    }
    event.preventDefault()
    form.setFields([{ name: field, errors: [errorMessage] }])
  }

  const onSendCode = async () => {
    try {
      const values = await form.validateFields(['enterpriseName', 'mobile'])
      await handleSendCode(normalizeSupplierIdentity(values))
    } catch {
      return
    }
  }

  const onSubmitLogin = async (values: SupplierSmsLoginParams) => {
    await handleClickLogin(normalizeSupplierLogin(values))
  }

  return (
    <div className={Style.layer}>
      <div className={Style.container}>
        <div className={Style.loginbody}>
          <div className={Style.loginbox}>
            <div
              className={Style.tip}
              style={{
                display:
                  new Date() <=
                  new Date(new Date().getFullYear(), 2, 19, 23, 59, 59)
                    ? 'block'
                    : 'none',
              }}
            >
              <div className={Style['tip-head']}>
                <WarningOutlined /> 【维护通知】
              </div>
              <div className={Style['tip-content']}>
                本系统将于本周四（[03-19]）19:00-22:00
                进行维护，期间服务暂停。期间请您不要使用系统，以免造成损失。对此带来的不便，敬请谅解。
              </div>
            </div>
            <div className={Style.formcontainer}>
              <div className={Style.logintitle}>咸亨国际SCP协同系统</div>
              {notice && (
                <Alert
                  className={Style.statusAlert}
                  closable
                  message={notice.message}
                  onClose={onNoticeClose}
                  showIcon
                  type={notice.type}
                />
              )}
              <Form
                autoComplete="on"
                className={Style.loginForm}
                form={form}
                initialValues={{
                  enterpriseName:
                    supplierName.length <= 200 ? supplierName : '',
                }}
                onFinish={onSubmitLogin}
                onValuesChange={onNoticeClose}
              >
                <Form.Item
                  name="enterpriseName"
                  rules={[
                    { validator: createValidator(validateEnterpriseName) },
                  ]}
                  className={Style.itembox}
                  validateTrigger={['onBlur', 'onSubmit']}
                >
                  <Input
                    autoComplete="on"
                    className={Style.iteminput}
                    maxLength={200}
                    onPaste={(event) =>
                      rejectOverlongPaste(
                        'enterpriseName',
                        200,
                        '供应商抬头最多输入200个字符',
                        event,
                      )
                    }
                    prefix={<BankOutlined className={Style.inputIcon} />}
                    placeholder="请输入供应商抬头"
                  />
                </Form.Item>
                <Form.Item
                  name="mobile"
                  rules={[{ validator: createValidator(validateMobile) }]}
                  className={Style.itembox}
                  validateTrigger={['onBlur', 'onSubmit']}
                >
                  <Input
                    autoComplete="on"
                    className={Style.iteminput}
                    inputMode="numeric"
                    maxLength={11}
                    onPaste={(event) =>
                      rejectOverlongPaste(
                        'mobile',
                        11,
                        '请输入正确的11位手机号',
                        event,
                      )
                    }
                    prefix={<MobileOutlined className={Style.inputIcon} />}
                    placeholder="请输入手机号"
                  />
                </Form.Item>
                <Form.Item className={Style.itembox}>
                  <Row gutter={8}>
                    <Col span={15}>
                      <Form.Item
                        name="code"
                        noStyle
                        rules={[
                          { validator: createValidator(validateSmsCode) },
                        ]}
                        validateTrigger={['onBlur', 'onSubmit']}
                      >
                        <Input
                          className={Style.codeinput}
                          maxLength={6}
                          onPaste={(event) =>
                            rejectOverlongPaste(
                              'code',
                              6,
                              '请输入6位短信验证码',
                              event,
                            )
                          }
                          prefix={
                            <SafetyCertificateOutlined
                              className={Style.inputIcon}
                            />
                          }
                          placeholder="请输入短信验证码"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={9}>
                      <Button
                        className={Style.sendButton}
                        disabled={!canSend}
                        loading={sendLoading}
                        onClick={onSendCode}
                      >
                        {countdown > 0 ? `${countdown}秒后重发` : '发送验证码'}
                      </Button>
                    </Col>
                  </Row>
                </Form.Item>
                <Form.Item noStyle>
                  <Button
                    className={Style.btnbox}
                    disabled={!canLogin}
                    htmlType="submit"
                    loading={loginLoading}
                  >
                    登录
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
